import React, { useState, useRef, useCallback } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  updateTitle,
  updateSlug,
  updateShortDescription,
  updateContent,
  updateThumbnailImage,
  updateFeaturedImage,
  updateAdditionalImages,
} from "../redux/news/actions";

// Register TableBlot so Quill preserves <table> tags, same as the Blog module
const BlockEmbed = Quill.import("blots/block/embed");
class TableBlot extends BlockEmbed {
  static create(value) {
    const node = super.create();
    node.innerHTML = value;
    return node;
  }
  static value(node) {
    return node.innerHTML;
  }
}
TableBlot.blotName = "table";
TableBlot.tagName = "table";
Quill.register(TableBlot);

const BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:5000";

// News admin routes are auth-protected on the backend — every call must
// attach this header, unlike the older Blog/Portfolio/Career modules.
const authHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("token")}`,
});

async function uploadImageFile(file) {
  const formData = new FormData();
  formData.append("image", file);
  const res = await fetch(`${BASE_URL}/api/news/upload-image`, {
    method: "POST",
    headers: authHeaders(),
    body: formData,
  });
  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.message || "Failed to upload image");
  }
  return data.data.url;
}

function AdminNews() {
  const quillRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { title, slug, shortDescription, content, thumbnailImage, featuredImage, additionalImages } = useSelector(
    (state) => state.news
  );

  const [formerror, setFormError] = useState({});
  const [featuredImgError, setFeaturedImgError] = useState("");
  const [featuredImgState, setFeaturedImgState] = useState("");
  const [thumbnailImgError, setThumbnailImgError] = useState("");
  const [thumbnailImgState, setThumbnailImgState] = useState("");
  const [additionalImgError, setAdditionalImgError] = useState("");
  const [additionalImgState, setAdditionalImgState] = useState("");

  const handleFeaturedImageChange = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;
    if (!selectedFile.type.startsWith("image/")) {
      setFeaturedImgError("Please upload an image file.");
      event.target.value = "";
      return;
    }
    setFeaturedImgState("Uploading image...");
    setFeaturedImgError("");
    try {
      const url = await uploadImageFile(selectedFile);
      dispatch(updateFeaturedImage(url));
      setFeaturedImgState("Image uploaded!");
    } catch (error) {
      setFeaturedImgError(error.message);
      setFeaturedImgState("");
    }
  };

  const handleThumbnailImageChange = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;
    if (!selectedFile.type.startsWith("image/")) {
      setThumbnailImgError("Please upload an image file.");
      event.target.value = "";
      return;
    }
    setThumbnailImgState("Uploading image...");
    setThumbnailImgError("");
    try {
      const url = await uploadImageFile(selectedFile);
      dispatch(updateThumbnailImage(url));
      setThumbnailImgState("Image uploaded!");
    } catch (error) {
      setThumbnailImgError(error.message);
      setThumbnailImgState("");
    }
  };

  const handleAdditionalImagesChange = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    setAdditionalImgState("Uploading images...");
    setAdditionalImgError("");
    try {
      const uploadedUrls = [];
      for (const file of files) {
        if (!file.type.startsWith("image/")) {
          setAdditionalImgError("Please upload image files only.");
          continue;
        }
        uploadedUrls.push(await uploadImageFile(file));
      }
      dispatch(updateAdditionalImages([...additionalImages, ...uploadedUrls]));
      setAdditionalImgState(uploadedUrls.length ? "Images uploaded!" : "");
    } catch (error) {
      setAdditionalImgError(error.message);
      setAdditionalImgState("");
    }
  };

  const handleRemoveAdditionalImage = (index) => {
    dispatch(updateAdditionalImages(additionalImages.filter((_, i) => i !== index)));
  };

  // Editor toolbar image upload
  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();
    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;
      try {
        const imageUrl = await uploadImageFile(file);
        const quill = quillRef.current.getEditor();
        const range = quill.getSelection(true);
        quill.insertEmbed(range ? range.index : 0, "image", imageUrl);
        quill.setSelection((range ? range.index : 0) + 1);
      } catch (error) {
        console.error("Image upload failed:", error);
      }
    };
  }, []);

  const validation = () => {
    let err = {};
    if (!title) err.title = "Please enter a title.";
    if (!slug) err.slug = "Please enter a slug.";
    if (!shortDescription) err.shortDescription = "Please enter a short description.";
    if (!thumbnailImage) err.thumbnailImage = "Please upload a thumbnail image.";
    if (!content) err.content = "Please enter the article content.";
    setFormError({ ...err });
    return Object.keys(err).length < 1;
  };

  const handleProceed = () => {
    if (validation()) navigate("/news/preview");
  };

  const modules = {
    toolbar: {
      container: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }, { font: [] }],
        [{ size: [] }],
        ["bold", "italic", "underline", "strike", "blockquote", "code-block"],
        [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
        [{ color: [] }, { background: [] }],
        [{ align: "" }, { align: "center" }, { align: "right" }, { align: "justify" }],
        ["link", "image", "video"],
        ["clean"],
      ],
      handlers: { image: imageHandler },
    },
    clipboard: { matchVisual: false },
  };

  const formats = [
    "header", "font", "size", "bold", "italic", "underline", "strike",
    "blockquote", "list", "bullet", "indent", "link", "image", "video",
    "align", "color", "background", "code-block", "table",
  ];

  return (
    <div className="container">
      <h3 className="fw-bold" style={{ marginBottom: "24px", color: "#1e3a8a", letterSpacing: "-0.5px" }}>
        Create News / Event
      </h3>

      {/* Title */}
      <div className="mb-4">
        <label className="fw-semibold mb-2 d-block">Title</label>
        <input type="text" className="form-control shadow-sm" placeholder="Enter title..."
          onChange={(e) => dispatch(updateTitle(e.target.value))} value={title}
          style={{ borderRadius: "6px", borderColor: "#d0d5dd", padding: "0.65rem 0.85rem" }} />
        <span className="text-danger small">{formerror.title}</span>
      </div>

      {/* Short Description */}
      <div className="mb-4">
        <label className="fw-semibold mb-2 d-block">Short Description</label>
        <textarea className="form-control shadow-sm" rows={3} placeholder="A short teaser shown on the News card..."
          onChange={(e) => dispatch(updateShortDescription(e.target.value))} value={shortDescription}
          style={{ borderRadius: "6px", borderColor: "#d0d5dd", padding: "0.65rem 0.85rem" }} />
        <span className="text-danger small">{formerror.shortDescription}</span>
      </div>

      {/* Thumbnail Image */}
      <div className="mb-4">
        <label className="fw-semibold mb-2 d-block">Thumbnail Image <span className="text-danger">*</span></label>
        <input type="file" accept="image/*" className="form-control shadow-sm" onChange={handleThumbnailImageChange}
          style={{ borderRadius: "6px", padding: "0.65rem 0.85rem", borderColor: "#d0d5dd" }} />
        <span className="text-danger small">{formerror.thumbnailImage}<br />{thumbnailImgError}</span>
        {thumbnailImage ? (
          <div className="mt-3">
            <label className="fw-semibold">Preview:</label>
            <img src={thumbnailImage} alt="Thumbnail" style={{ width: "100%", maxWidth: "450px", borderRadius: "10px", marginTop: "6px" }} />
          </div>
        ) : <div className="text-muted">{thumbnailImgState}</div>}
      </div>

      {/* Featured Image */}
      <div className="mb-4">
        <label className="fw-semibold mb-2 d-block">Featured Image (optional)</label>
        <input type="file" accept="image/*" className="form-control shadow-sm" onChange={handleFeaturedImageChange}
          style={{ borderRadius: "6px", padding: "0.65rem 0.85rem", borderColor: "#d0d5dd" }} />
        <span className="text-danger small">{formerror.featuredImage}<br />{featuredImgError}</span>
        {featuredImage ? (
          <div className="mt-3">
            <label className="fw-semibold">Preview:</label>
            <img src={featuredImage} alt="Featured" style={{ width: "100%", maxWidth: "450px", borderRadius: "10px", marginTop: "6px" }} />
          </div>
        ) : <div className="text-muted">{featuredImgState}</div>}
      </div>

      {/* Slug */}
      <div className="mb-3" style={{ background: "#f0f7ff", padding: "14px", borderRadius: "6px", fontSize: "14px", color: "#1e3a8a" }}>
        <ul className="mb-0 ps-3">
          <li>Slug is the URL path name for this News / Event</li>
          <li>Example: https://stellarmind.ai/news/[slug-name]</li>
          <li>No spaces or uppercase letters — use hyphens (-)</li>
        </ul>
      </div>
      <div className="mb-4">
        <label className="fw-semibold mb-2 d-block">Slug</label>
        <input type="text" className="form-control shadow-sm" placeholder="enter-slug"
          onChange={(e) => dispatch(updateSlug(e.target.value))} value={slug}
          style={{ borderRadius: "6px", borderColor: "#d0d5dd", padding: "0.65rem 0.85rem" }} />
        <span className="text-danger small">{formerror.slug}</span>
      </div>

      {/* Additional Images */}
      <div className="mb-4">
        <label className="fw-semibold mb-2 d-block">Additional Article Images (optional)</label>
        <input type="file" accept="image/*" multiple className="form-control shadow-sm" onChange={handleAdditionalImagesChange}
          style={{ borderRadius: "6px", padding: "0.65rem 0.85rem", borderColor: "#d0d5dd" }} />
        <span className="text-danger small">{additionalImgError}</span>
        {additionalImages.length > 0 && (
          <div className="mt-3 d-flex flex-wrap gap-3">
            {additionalImages.map((img, index) => (
              <div key={index} style={{ position: "relative" }}>
                <img src={img} alt={`Additional ${index + 1}`} style={{ width: "140px", height: "100px", objectFit: "cover", borderRadius: "8px" }} />
                <button type="button" onClick={() => handleRemoveAdditionalImage(index)}
                  className="btn btn-sm btn-danger"
                  style={{ position: "absolute", top: "-8px", right: "-8px", borderRadius: "50%", padding: "2px 8px" }}>
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
        {!additionalImages.length && <div className="text-muted">{additionalImgState}</div>}
      </div>

      {/* Content Image Upload note */}
      <div className="mb-3" style={{ background: "#f0f7ff", padding: "14px", borderRadius: "6px", fontSize: "14px", color: "#1e3a8a" }}>
        <strong>Images:</strong> Click the 🖼️ image button in the editor toolbar to upload &amp; insert images directly into the article content.
      </div>

      {/* Article Content Editor */}
      <div className="mb-4">
        <label className="fw-semibold mb-2 d-block">Article Content</label>
        <ReactQuill ref={quillRef} theme="snow" modules={modules} formats={formats}
          onChange={(value) => dispatch(updateContent(value))} value={content}
          style={{ minHeight: "240px", borderRadius: "6px" }} />
        <span className="text-danger small">{formerror.content}</span>
      </div>

      <button onClick={handleProceed} className="btn btn-primary w-100 fw-semibold"
        style={{ background: "linear-gradient(90deg, #1e3a8a, #2563eb)", color: "white", padding: "14px", borderRadius: "8px", fontSize: "17px", border: "none" }}>
        Proceed
      </button>
    </div>
  );
}

export default AdminNews;
